import React, { type ReactNode } from "react";
import { classes, platformCopy, type SemanticTone } from "./shared";
import { StatusLabel } from "./data";
import { SectionHeader } from "./structure";

function timelineCopy(value: ReactNode): ReactNode {
  return typeof value === "string" ? platformCopy(value) : value;
}

export function IdentityHeader({
  eyebrow,
  title,
  relationship,
  status,
  warning,
  nextAction,
  actions,
  className
}: {
  eyebrow?: string;
  title: ReactNode;
  relationship?: ReactNode;
  status?: ReactNode;
  warning?: ReactNode;
  nextAction?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <header className={classes("ry-identity-header", className)}>
      <div>
        {eyebrow ? <p className="ry-identity-eyebrow">{eyebrow}</p> : null}
        <div className="ry-identity-heading">
          <h1>{title}</h1>
          {status}
        </div>
        {relationship ? <p>{relationship}</p> : null}
        {warning ? <div className="ry-identity-warning">{warning}</div> : null}
        {nextAction ? <div className="ry-identity-next-action"><strong>Next action</strong>{nextAction}</div> : null}
      </div>
      {actions ? <div className="ry-identity-actions">{actions}</div> : null}
    </header>
  );
}

export interface TimelineEntry {
  id: string;
  title: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  status?: ReactNode;
}

export interface AuditEntry {
  id: string;
  action: ReactNode;
  actor?: ReactNode;
  outcome?: ReactNode;
  timestamp: ReactNode;
  detail?: ReactNode;
}

export function AuditHistory({
  entries,
  empty = "No audited outcome has been recorded.",
  label = "Audit history",
  className
}: {
  entries: AuditEntry[];
  empty?: ReactNode;
  label?: string;
  className?: string;
}) {
  if (!entries.length) return <p className="empty">{empty}</p>;
  return (
    <ol className={classes("ry-audit-history", className)} aria-label={label}>
      {entries.map((entry) => (
        <li key={entry.id}>
          <div>
            <strong>{entry.action}</strong>
            {entry.detail ? <p>{entry.detail}</p> : null}
            <small>{entry.timestamp}{entry.actor ? <> · {entry.actor}</> : null}</small>
          </div>
          {entry.outcome}
        </li>
      ))}
    </ol>
  );
}

export function ActivityTimeline({
  entries,
  empty = "No activity recorded.",
  label = "Activity timeline",
  className
}: {
  entries: TimelineEntry[];
  empty?: ReactNode;
  label?: string;
  className?: string;
}) {
  if (!entries.length) return <p className="empty">{empty}</p>;
  return (
    <ol className={classes("ry-activity-timeline", className)} aria-label={label}>
      {entries.map((entry) => (
        <li key={entry.id}>
          <div>
            <strong>{timelineCopy(entry.title)}</strong>
            {entry.description ? <p>{timelineCopy(entry.description)}</p> : null}
            {entry.meta ? <small>{entry.meta}</small> : null}
          </div>
          {entry.status}
        </li>
      ))}
    </ol>
  );
}

function DomainIndicator({
  label,
  value,
  tone,
  rationale,
  className
}: {
  label: string;
  value: string;
  tone?: SemanticTone;
  rationale?: ReactNode;
  className?: string;
}) {
  return (
    <span className={classes("ry-domain-indicator", className)}>
      <span className="sr-only">{label}: </span>
      <StatusLabel value={value} {...(tone ? { tone } : {})} />
      {rationale ? <small>{rationale}</small> : null}
    </span>
  );
}

export function RiskIndicator(props: Omit<Parameters<typeof DomainIndicator>[0], "label">) {
  return <DomainIndicator {...props} label="Risk" className={classes("ry-risk-indicator", props.className)} />;
}

export function AuthorityIndicator(props: Omit<Parameters<typeof DomainIndicator>[0], "label">) {
  return <DomainIndicator {...props} label="Authority" className={classes("ry-authority-indicator", props.className)} />;
}

const evidenceTone: Record<string, SemanticTone> = {
  verified_fact: "success",
  direct_evidence: "success",
  strong_proxy: "info",
  weak_proxy: "warning",
  estimate: "warning",
  assumption: "warning",
  model_generated_inference: "ai",
  model_inference: "ai",
  unknown: "neutral"
};

export function EvidenceLabel({
  value,
  confidence,
  freshness,
  className
}: {
  value: string;
  confidence?: string;
  freshness?: string;
  className?: string;
}) {
  return (
    <span className={classes("ry-evidence-label", className)}>
      <StatusLabel value={value} tone={evidenceTone[value] ?? "neutral"} />
      {confidence ? <small>Confidence: {confidence}</small> : null}
      {freshness ? <small>{freshness}</small> : null}
    </span>
  );
}

export function NotificationItem({
  title,
  reason,
  timestamp,
  unread = false,
  severity = "info",
  action
}: {
  title: string;
  reason: ReactNode;
  timestamp: ReactNode;
  unread?: boolean;
  severity?: SemanticTone;
  action?: ReactNode;
}) {
  return (
    <article className={classes("ry-notification-item", unread && "ry-notification-unread")}>
      <div>
        <StatusLabel value={severity} tone={severity} />
        <strong>{title}</strong>
        <p>{reason}</p>
        <small>{timestamp}</small>
      </div>
      {action}
    </article>
  );
}

export function TaskItem({
  title,
  due,
  origin,
  status,
  blockedReason,
  action
}: {
  title: string;
  due?: ReactNode;
  origin?: ReactNode;
  status: string;
  blockedReason?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <article className={classes("ry-task-item", status === "blocked" && "ry-task-blocked")}>
      <div>
        <strong>{title}</strong>
        {due ? <small>{due}</small> : null}
        {origin ? <small>Origin: {origin}</small> : null}
        {blockedReason ? <p>{blockedReason}</p> : null}
      </div>
      <div><StatusLabel value={status} />{action}</div>
    </article>
  );
}

export function AIRecommendation({
  title,
  status,
  children,
  evidence,
  limitations,
  actions
}: {
  title: string;
  status: string;
  children: ReactNode;
  evidence?: ReactNode;
  limitations?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <article className="ry-ai-recommendation">
      <SectionHeader eyebrow="AI-assisted · review required" title={title} action={<span className="ry-plain-status">{status.replaceAll("_", " ")}</span>} />
      <div className="ry-ai-content">{children}</div>
      {evidence ? <section aria-label="Supporting evidence"><strong>Supporting evidence</strong>{evidence}</section> : null}
      {limitations ? <section aria-label="Known limitations"><strong>Known limitations</strong>{limitations}</section> : null}
      {actions}
    </article>
  );
}

export function ApprovalPanel({
  title,
  readiness,
  consequence,
  rationale,
  actions,
  error,
  processing = false
}: {
  title: string;
  readiness: ReactNode;
  consequence: ReactNode;
  rationale?: ReactNode;
  actions: ReactNode;
  error?: ReactNode;
  processing?: boolean;
}) {
  return (
    <section className="ry-approval-panel" aria-busy={processing || undefined}>
      <SectionHeader eyebrow="Decision" title={title} />
      <div><strong>Readiness</strong>{readiness}</div>
      <div><strong>Exact consequence</strong>{consequence}</div>
      {rationale ? <div><strong>Required rationale</strong>{rationale}</div> : null}
      {error ? <div className="ry-field-error-text" role="alert">{error}</div> : null}
      <div className="ry-button-group">{actions}</div>
    </section>
  );
}
